import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadPdfLogoDataUrl } from "./pdfLogo";
import { BRANDING } from "./branding";

import { formatJalaliDate } from "../shop/utils/dateFormat";
const extractAmountValue = (value, preferredCurrency) => {
  if (value == null) return 0;

  if (typeof value === "number" || typeof value === "string") {
    return value;
  }

  if (typeof value === "object") {
    const selectedValue =
      preferredCurrency && value[preferredCurrency] != null
        ? value[preferredCurrency]
        : Object.values(value)[0];

    if (selectedValue && typeof selectedValue === "object") {
      return (
        selectedValue.source ??
        selectedValue.parsedValue ??
        selectedValue.amount ??
        0
      );
    }

    return selectedValue ?? 0;
  }

  return 0;
};

const parseAmount = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return value;

  const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getAmount = (value, preferredCurrency) =>
  parseAmount(extractAmountValue(value, preferredCurrency));

const getSaleReturnAmount = (sale) => {
  if (sale?.report_return_amount != null) {
    return parseAmount(sale.report_return_amount);
  }

  return (sale?.sale_returns || []).reduce((sum, saleReturn) => {
    const itemRefundAmount = (saleReturn?.return_items || []).reduce(
      (itemSum, item) => itemSum + parseAmount(item.refund_amount),
      0
    );

    return (
      sum +
      (parseAmount(saleReturn?.total_refound_amount) || itemRefundAmount)
    );
  }, 0);
};

export async function exportSalesToPDF(sales, filename = "sales.pdf") {
  // Initialize PDF in portrait (A4)
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let currentY = 10;
  const logoDataUrl = await loadPdfLogoDataUrl();

  // Document properties
  doc.setProperties({
    title: "Sales",
    subject: "Sales Data Export",
    creator: "Tawakal Noorzai Trading Company",
  });

  // Format currency helper
  const formatCurrency = (value, currency) =>
    `${parseAmount(value).toFixed(2)} ${currency || ""}`;

  // --- HEADER (identical to purchase) ---
  const addHeader = () => {
    const shopName = BRANDING.companyNameEn;

    // Logo
    try {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, "JPEG", pageWidth - 25, 2, 10, 10);
      }
    } catch (error) {
      console.error("Failed to add logo:", error);
    }

    // Title
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 102, 204);
    doc.text("Sales Report", pageWidth / 2, 9, { align: "center" });

    // Branch name
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(80, 80, 80);
    doc.text(shopName, 10, 7);

    // Date
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${formatJalaliDate()}`, 10, 11);

    return 18; // Header height
  };

  currentY = addHeader();

  // Calculate summary totals
  const summaryTotals = sales.reduce((acc, sale) => {
    const currency = sale.currency_details?.title || "";
    const totalAmount =
      sale.report_total_amount != null
        ? parseAmount(sale.report_total_amount)
        : getAmount(sale.total_amount, currency);
    const totalPaid =
      sale.report_paid_amount != null
        ? parseAmount(sale.report_paid_amount)
        : getAmount(sale.received_amount ?? sale.total_paid, currency);
    const totalReturn = getSaleReturnAmount(sale);
    const totalRemained =
      sale.report_remaining_amount != null
        ? parseAmount(sale.report_remaining_amount)
        : Math.max(0, totalAmount - totalPaid - totalReturn);

    return {
      totalAmount: acc.totalAmount + totalAmount,
      totalPaid: acc.totalPaid + totalPaid,
      totalReturn: acc.totalReturn + totalReturn,
      totalRemained: acc.totalRemained + totalRemained,
      currency: currency || acc.currency,
    };
  }, {
    totalAmount: 0,
    totalPaid: 0,
    totalReturn: 0,
    totalRemained: 0,
    currency: "",
  });

  // --- TABLES ---
  sales.forEach((sale) => {
    // Page break check
    if (currentY > pageHeight - 60) {
      doc.addPage();
      currentY = addHeader();
    }

    const currency = sale.currency_details?.title || "";
    const totalAmount =
      sale.report_total_amount != null
        ? parseAmount(sale.report_total_amount)
        : getAmount(sale.total_amount, currency);
    const totalPaid =
      sale.report_paid_amount != null
        ? parseAmount(sale.report_paid_amount)
        : getAmount(sale.received_amount ?? sale.total_paid, currency);
    const totalReturn = getSaleReturnAmount(sale);
    const totalRemained =
      sale.report_remaining_amount != null
        ? parseAmount(sale.report_remaining_amount)
        : Math.max(0, totalAmount - totalPaid - totalReturn);

    // Main Sales Table (blue header)
    // Main Sales Table (blue header)
    autoTable(doc, {
      head: [
        [
          "ID",
          "Customer",
          "Date",
          "Discount",
          "Total Amount",
          "Paid Amount",
          "Return Amount",
          "Remaining",
          "Items",
        ],
      ],
      body: [
        [
          sale.id || "N/A",
          sale.customer_details?.name || "N/A",
          sale.sale_date || "N/A",
          `${sale.discount || 0}`,
          formatCurrency(totalAmount, currency),
          formatCurrency(totalPaid, currency),
          formatCurrency(totalReturn, currency),
          formatCurrency(totalRemained, currency),
          sale.sale_items?.length || 0,
        ],
      ],
      startY: currentY,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },
      headStyles: {
        fontSize: 8,
        fontStyle: "bold",
        fillColor: [51, 102, 204],
        textColor: [255, 255, 255],
        halign: "center",
        cellPadding: 2,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 250],
      },
      margin: { top: 1, bottom: 1, left: 3, right: 3 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) currentY = addHeader();
      },
    });

    // ✅ Match purchase structure: add spacing between tables
    currentY = doc.lastAutoTable.finalY + 0;

    // Sale Items Table (light blue header)
    if (sale.sale_items?.length) {
      autoTable(doc, {
        head: [["Product", "Qty", "Sale Price", "Total", "Unit"]],
        body: sale.sale_items.map((item) => [
          item.product_details?.name || "N/A",
          item.quantity || 0,
          formatCurrency(item.sale_price, currency),
          formatCurrency(item.quantity * item.sale_price, currency),
          item.unit_details?.title || "N/A",
        ]),
        startY: currentY,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          halign: "center",
          valign: "middle",
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
        },
        headStyles: {
          fontSize: 8,
          fontStyle: "bold",
          fillColor: [220, 230, 242],
          textColor: [50, 50, 50],
          halign: "center",
          cellPadding: 2,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        margin: { top: 1, bottom: 1, left: 3, right: 3 },
      });
      currentY = doc.lastAutoTable.finalY + 2;
    }
  });

  // Add summary table
  if (currentY > pageHeight - 40) {
    doc.addPage();
    currentY = addHeader();
  }

  autoTable(doc, {
    head: [[
      "Summary",
      "Total Amount",
      "Paid Amount",
      "Return Amount",
      "Remaining Total Amount",
    ]],
    body: [[
      "TOTALS",
      `${summaryTotals.totalAmount.toFixed(2)} ${summaryTotals.currency}`,
      `${summaryTotals.totalPaid.toFixed(2)} ${summaryTotals.currency}`,
      `${summaryTotals.totalReturn.toFixed(2)} ${summaryTotals.currency}`,
      `${summaryTotals.totalRemained.toFixed(2)} ${summaryTotals.currency}`
    ]],
    startY: currentY + 5,
    styles: {
      fontSize: 9,
      cellPadding: 3,
      halign: "center",
      fontStyle: "bold",
    },
    headStyles: {
      fontSize: 10,
      fontStyle: "bold",
      fillColor: [51, 102, 204],
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fillColor: [245, 245, 245],
      fontStyle: "bold",
    },
    margin: { left: 3, right: 3 },
  });

  // --- FOOTER (identical to purchase) ---
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Divider line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(0, pageHeight - 11, pageWidth - 0, pageHeight - 11);

    // Footer text
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(BRANDING.companyNameEn, pageWidth / 2, pageHeight - 5, {
      align: "center",
    });

    // Page number
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 15, pageHeight - 5, {
      align: "right",
    });
  }

  doc.save(filename);
}


